import { useContext, useMemo } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

import useUserPermissionsContext from "./hooks/useUserPermissionsContext";
import UserRights from "./enums/UserRights";
import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { GlobalContext } from "./globalContext";
import { formatPageTitle } from "./utils/pageTitle";
import { getCreateTitle, isExcludedFromGlobalFiltering } from "./utils/utils";

const PrivateRoute = ({ entity, children }) => {
  const { isAllowedTo, isLoading, hasError } = useUserPermissionsContext();
  const { globalFilters } = useContext(GlobalContext);
  const location = useLocation();

  /**
   * It is assumed that a user with create authorisation
   * will also have read authorisation
   */
  const isAllowedToCreate = isAllowedTo(UserRights.CREATE, entity);
  const isAllowedToRead = isAllowedTo(UserRights.READ, entity);

  // Check if current route is a create route
  const isCreateRoute = useMemo(
    () => location.pathname.includes("/create"),
    [location.pathname]
  );

  // Entity create screens that should not be affected by global filtering
  const isExcludedFromGlobalObjectFiltering = useMemo(
    () => isExcludedFromGlobalFiltering(location.pathname, "create"),
    [location.pathname]
  );

  const pageTitle = formatPageTitle("BS Frontend");

  const dynamicPageTitle = isCreateRoute
    ? formatPageTitle(getCreateTitle(entity))
    : pageTitle;

  if (isLoading) {
    return (
      <Box
        sx={{
          marginTop: 3,
          textAlign: "center",
        }}
      >
        <CircularProgress sx={{ marginBottom: "2rem" }} />
        <Typography>Loading your permissions</Typography>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Alert severity='error'>
        <Typography>
          There was an error loading your permissions. Please try again later.
        </Typography>
      </Alert>
    );
  }

  // Check for global object filtering on create routes
  if (
    isCreateRoute &&
    !globalFilters?.value &&
    !isExcludedFromGlobalObjectFiltering &&
    (isAllowedToCreate || isAllowedToRead)
  ) {
    return (
      <Alert severity='warning'>
        <Helmet>
          <title>{dynamicPageTitle}</title>
        </Helmet>
        Please choose an object in the upper right list to create new records.
      </Alert>
    );
  }

  return (
    <>
      {isAllowedToCreate || isAllowedToRead ? (
        children
      ) : (
        <Redirect to='/unauthorised' />
      )}
    </>
  );
};

export default PrivateRoute;
